import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { ApolloError } from "@apollo/client";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PropertyForm, { PropertyFormOutput, uploadPropertyFormData } from "components/property-form/property-form";

import { useCreatePropertyMutation } from "graphql/mutations/create-property.generated";

import routeNames from "constants/route-names";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAuth } from "context/AuthContext";

const PropertyAdd: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const { refreshToken } = useAuth();
	const navigate = useNavigate();
	const [uploading, setUploading] = useState<boolean>(false);

	const handleOnCompleteCreateProperty = useCallback(async () => {
		if (refreshToken) await refreshToken();
		navigate(routeNames.ADMIN_PROPERTIES);
	}, [refreshToken, navigate]);

	const handleOnErrorCreateProperty = useCallback(
		(_err: ApolloError) => {
			navigate(routeNames.ADMIN_PROPERTIES);
		},
		[navigate]
	);

	const [createPropertyMutation, { loading: mutationLoading }] = useCreatePropertyMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorCreateProperty,
		onCompleted: handleOnCompleteCreateProperty
	});

	// ── Submit: upload to Firebase then call mutation ──────────────────────────
	const handleSubmit = useCallback(
		async (output: PropertyFormOutput) => {
			setUploading(true);
			try {
				// The backend handle the address insertion in the same transaction
				const propertyInput = await uploadPropertyFormData(output, loggedUserValue);
				createPropertyMutation({ variables: { input: propertyInput } });
			} catch (err) {
				console.error("Error building property input:", err);
				message.error("Failed to create property.");
			} finally {
				setUploading(false);
			}
		},
		[loggedUserValue, createPropertyMutation]
	);

	const isLoading = uploading || mutationLoading;

	return (
		<div className="add-product-screen-container">

			<div className="pa-form-container">
				<span className="add-product-page-title">{"New Property"}</span>

				<PropertyForm submitLabel="Save" externalLoading={isLoading} onSubmit={handleSubmit} />
			</div>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default PropertyAdd;
